import { useQuery } from '@tanstack/react-query'

import { API } from '@/api'
import { Instance } from '@/models/instance'
import { TIME_UNITS_IN_MILLS } from '@/utils/time'

interface useTeamMemberInstanceListQueryParams {
  teamResID: string
  memberResID: string
  page: number
  pageSize: number
  statusCodes?: number[]
  regions: number[]
  regionFilterCriteria: number
}

export function useTeamMemberInstanceListQuery({
  teamResID,
  memberResID,
  page,
  pageSize,
  statusCodes,
  regions,
  regionFilterCriteria,
}: useTeamMemberInstanceListQueryParams) {
  const queryResult = useQuery({
    // queryKey: All parameters that affect the data
    // React Query automatically compares these and cancels old requests when they change
    queryKey: [
      'teamMemberInstanceList',
      teamResID,
      memberResID,
      page,
      pageSize,
      statusCodes?.join(','),
      regions.join(','),
      regionFilterCriteria,
    ],

    // queryFn: The actual request function
    // { signal } is automatically injected by React Query for request cancellation
    queryFn: async ({ signal }) => {
      let apiRegionFilterCriteria = 0
      const REGION_DATA_DRIVE = -2
      const REGION_MESH_CLUSTERING = -3

      if (regionFilterCriteria === 0 || regionFilterCriteria === -1) {
        apiRegionFilterCriteria = 0
      } else if (regionFilterCriteria === REGION_DATA_DRIVE) {
        apiRegionFilterCriteria = 1
      } else if (regionFilterCriteria === REGION_MESH_CLUSTERING) {
        apiRegionFilterCriteria = 2
      }

      // Pass signal to API call for automatic cancellation
      const res = await API.getTeamMemberInstances(
        {
          res_id: teamResID,
          member_res_ids: [memberResID],
          page,
          per_page: pageSize,
          statuses: statusCodes && statusCodes.length ? statusCodes : undefined,
          regions: regions,
          region_filter_criteria: apiRegionFilterCriteria,
        },
        { signal },
      )

      if (res.code !== 0) {
        throw new Error(`Failed to fetch team member instances: ${res.msg}`)
      }

      const instances = (res.instances ?? []).map((row) => new Instance(row.instance))

      return { instances, pagination: res.pagination }
    },

    // Dynamic refetch interval based on instance status
    // Fast polling (1s) when instances are in progress, otherwise 5s
    refetchInterval: (query) => {
      const instances = query.state.data?.instances ?? []
      const hasInProgress = instances.some((item) => item.inProgress)
      return hasInProgress ? TIME_UNITS_IN_MILLS.SECOND : 5 * TIME_UNITS_IN_MILLS.SECOND
    },

    // Data is always considered stale (immediate refetch on focus/mount)
    staleTime: 0,

    // Query is always enabled
    enabled: !!teamResID && !!memberResID,
  })

  return {
    instances: queryResult.data?.instances ?? [],
    total: queryResult.data?.pagination.total ?? 0,
    currentPage: queryResult.data?.pagination.page ?? page,
    isLoading: queryResult.isLoading,
    isError: queryResult.isError,
    error: queryResult.error,
    refetch: queryResult.refetch,
  }
}
